import { get } from 'lodash';

export type MessageIssueSeverity = 'warning' | 'error';

export type MessageIssueCode =
  | 'no_messages'
  | 'message_name_blank'
  | 'message_type_blank'
  | 'duplicate_message'
  | 'read_tag_not_found_in_scan'
  | 'parameter_id_blank'
  | 'duplicate_parameter'
  | 'parameter_id_not_found_in_scan'
  | 'parameter_key_duplicate'
  | 'parameter_field_pattern_invalid'
  | 'scan_result_missing';

// Returns field keys from message properties marked x-scanValidate:true; falls back to ['trigger'].
const getScanValidatableMessageFields = (messageProperties: Record<string, any>): string[] => {
  const fromSchema = Object.keys(messageProperties).filter(
    (key) => key !== 'props' && get(messageProperties[key], 'x-scanValidate') === true
  );
  return fromSchema.length > 0 ? fromSchema : ['trigger'];
};

// Returns field keys from param properties marked x-scanValidate:true; falls back to ['id'].
const getScanValidatableParamFields = (paramProperties: Record<string, any>): string[] => {
  const fromSchema = Object.keys(paramProperties).filter(
    (key) => get(paramProperties[key], 'x-scanValidate') === true
  );
  return fromSchema.length > 0 ? fromSchema : ['id'];
};

export type MessageIssue = {
  id: string;
  severity: MessageIssueSeverity;
  code: MessageIssueCode;
  text: string;
  /** Short version of text for inline helper display. */
  shortText?: string;
  assetIndex: number;
  componentIndex: number;
  messageIndex?: number;
  propIndex?: number;
  assetName: string;
  assetIp: string;
  componentName: string;
  messageName: string;
  currentValue?: string;
  editableField?: 'message_name' | 'message_type' | 'read' | 'prop_id' | 'prop_field';
  /** Actual param field key (e.g. 'key', 'uom') when editableField === 'prop_field'. */
  editableParamField?: string;
};

const isNonEmpty = (value: any) => String(value ?? '').trim().length > 0;

const assetLabel = (name: string, ip: string, fallbackIndex: number) => name || ip || `Asset ${fallbackIndex + 1}`;

const extractScannedTagKeys = (scanResult: any): Set<string> => {
  const keys = new Set<string>();
  if (!Array.isArray(scanResult)) return keys;

  scanResult.forEach((item: any) => {
    if (item === null || item === undefined) return;
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
      const token = String(item).trim().toLowerCase();
      if (token) keys.add(token);
      return;
    }
    if (typeof item !== 'object') return;

    const candidates = [
      get(item, 'name'),
      get(item, 'Name'),
      get(item, 'tag'),
      get(item, 'key'),
      get(item, 'id'),
      get(item, 'nodeId'),
      get(item, 'address'),
      get(item, 'displayName'),
    ];

    candidates.forEach((candidate) => {
      const token = String(candidate ?? '').trim().toLowerCase();
      if (token) keys.add(token);
    });
  });

  return keys;
};

export const collectMessageValidationIssues = (
  assets: any[],
  componentSchemaMap?: Record<string, { ui_schema?: any; backend_schema?: any }>
): MessageIssue[] => {
  const issues: MessageIssue[] = [];

  (assets || []).forEach((asset: any, assetIndex: number) => {
    const assetName = String(get(asset, 'data.name', '')).trim();
    const assetIp = String(get(asset, 'data.ip', '')).trim();
    const components = Array.isArray(get(asset, 'data.options')) ? get(asset, 'data.options') : [];

    components.forEach((component: any, componentIndex: number) => {
      const componentName = String(get(component, 'component_name', get(component, 'label', ''))).trim();
      const messages = Array.isArray(get(component, 'data.messages')) ? get(component, 'data.messages') : [];
      const scanResult = get(component, 'data.scan_result', []);
      const scannedTagKeys = extractScannedTagKeys(scanResult);
      const hasScanResult = scannedTagKeys.size > 0;

      // Resolve schema-driven scan-validatable fields for this component.
      const componentNameKey = componentName.trim().toLowerCase();
      const schemaEntry = componentSchemaMap?.[componentNameKey] || {};
      const backendSchema = get(component, 'backend_schema') || get(schemaEntry, 'backend_schema') || {};
      const parsedSchema = backendSchema && typeof backendSchema === 'string' ? (() => { try { return JSON.parse(backendSchema); } catch { return {}; } })() : (backendSchema || {});
      const messageProperties: Record<string, any> = get(parsedSchema, 'properties.messages.items.properties', {});
      const paramProperties: Record<string, any> = get(messageProperties, 'props.items.properties', {});
      const scanValidatableMessageFields = getScanValidatableMessageFields(messageProperties);
      const scanValidatableParamFields = getScanValidatableParamFields(paramProperties);

      if (messages.length > 0 && !hasScanResult) {
        issues.push({
          id: `a${assetIndex}-c${componentIndex}-scan-result-missing`,
          severity: 'warning',
          code: 'scan_result_missing',
          text: 'Scan results unavailable.',
          assetIndex,
          componentIndex,
          assetName,
          assetIp,
          componentName,
          messageName: '',
        });
      }

      if (messages.length === 0) {
        issues.push({
          id: `a${assetIndex}-c${componentIndex}-m-none-no_messages`,
          severity: 'warning',
          code: 'no_messages',
          text: 'No messages found.',
          assetIndex,
          componentIndex,
          assetName,
          assetIp,
          componentName,
          messageName: '',
        });
        return;
      }

      const seenMessageNames = new Set<string>();
      messages.forEach((message: any, messageIndex: number) => {
        const messageName = String(get(message, 'name', get(message, 'id', ''))).trim();
        const messageType = String(get(message, 'type', '')).trim();
        const messageKey = messageName.toLowerCase();

        if (!isNonEmpty(messageName)) {
          issues.push({
            id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-name-blank`,
            severity: 'error',
            code: 'message_name_blank',
            text: 'Message name is required.',
            assetIndex,
            componentIndex,
            messageIndex,
            assetName,
            assetIp,
            componentName,
            messageName,
            editableField: 'message_name',
            currentValue: messageName,
          });
        }

        if (!isNonEmpty(messageType)) {
          issues.push({
            id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-type-blank`,
            severity: 'error',
            code: 'message_type_blank',
            text: 'Message type is required.',
            assetIndex,
            componentIndex,
            messageIndex,
            assetName,
            assetIp,
            componentName,
            messageName,
            editableField: 'message_type',
            currentValue: messageType,
          });
        }

        if (messageKey) {
          if (seenMessageNames.has(messageKey)) {
            issues.push({
              id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-duplicate-message`,
              severity: 'error',
              code: 'duplicate_message',
              text: `Duplicate message: "${messageName}".`,
              assetIndex,
              componentIndex,
              messageIndex,
              assetName,
              assetIp,
              componentName,
              messageName,
              editableField: 'message_name',
              currentValue: messageName,
            });
          }
          seenMessageNames.add(messageKey);
        }

        const messageReadTag = String(get(message, scanValidatableMessageFields[0], '')).trim();
        const allReadTags = scanValidatableMessageFields
          .map((field) => ({ field, value: String(get(message, field, '')).trim() }))
          .filter(({ value }) => isNonEmpty(value));

        allReadTags.forEach(({ value: readTagValue }) => {
          if (hasScanResult) {
            const normalizedReadTag = readTagValue.toLowerCase();
            if (!scannedTagKeys.has(normalizedReadTag)) {
              issues.push({
                id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-read-tag-not-found-${normalizedReadTag}`,
                severity: 'error',
                code: 'read_tag_not_found_in_scan',
                text: `Invalid read tag: "${readTagValue}".`,
                assetIndex,
                componentIndex,
                messageIndex,
                assetName,
                assetIp,
                componentName,
                messageName,
                editableField: 'read',
                currentValue: readTagValue,
              });
            }
          }
        });

        const props = Array.isArray(get(message, 'props')) ? get(message, 'props') : [];
        const seenProps = new Set<string>();

        // Schema-driven array-level uniqueness constraints (e.g. uniqueItemProperties: ["key"]).
        const propArraySchema = get(messageProperties, 'props', {});
        const uniqueByFields: string[] = Array.isArray(get(propArraySchema, 'uniqueItemProperties'))
          ? get(propArraySchema, 'uniqueItemProperties')
          : [];
        const seenByField = new Map<string, Set<string>>();
        uniqueByFields.forEach((field: string) => seenByField.set(field, new Set()));

        props.forEach((prop: any, propIndex: number) => {
          // Use the first scan-validatable param field as the identity/ID field.
          const idField = scanValidatableParamFields[0] ?? 'id';
          const propId = String(get(prop, idField, '')).trim();

          // Schema-driven duplicate signature; fallback preserves id::key semantics.
          const sigFields = Object.keys(paramProperties).length > 0
            ? Object.keys(paramProperties).sort()
            : [idField, 'key'];
          const signature = sigFields.map((f) => String(get(prop, f, '')).trim().toLowerCase()).join('::');
          // Secondary display field for duplicate error text — first non-identity field, fallback 'key'.
          const secondaryField = sigFields.find((f) => f !== idField) ?? 'key';
          const propKey = String(get(prop, secondaryField, '')).trim();

          if (!isNonEmpty(propId)) {
            issues.push({
              id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-p${propIndex}-id-blank`,
              severity: 'error',
              code: 'parameter_id_blank',
              text: 'Parameter ID is required.',
              shortText: 'Parameter ID is required.',
              assetIndex,
              componentIndex,
              messageIndex,
              propIndex,
              assetName,
              assetIp,
              componentName,
              messageName,
              editableField: 'prop_id',
              editableParamField: idField,
              currentValue: propId,
            });
          }

          if (isNonEmpty(propId) && isNonEmpty(propKey) && seenProps.has(signature)) {
            issues.push({
              id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-p${propIndex}-duplicate-parameter`,
              severity: 'error',
              code: 'duplicate_parameter',
              text: `Duplicate parameter: (${propId}, ${propKey}).`,
              assetIndex,
              componentIndex,
              messageIndex,
              propIndex,
              assetName,
              assetIp,
              componentName,
              messageName,
              editableField: 'prop_id',
              currentValue: propId,
            });
          }

          if (isNonEmpty(propId) || isNonEmpty(propKey)) {
            seenProps.add(signature);
          }

          if (hasScanResult && isNonEmpty(propId)) {
            const normalizedPropId = propId.toLowerCase();
            if (!scannedTagKeys.has(normalizedPropId)) {
              issues.push({
                id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-p${propIndex}-id-not-found`,
                severity: 'error',
                code: 'parameter_id_not_found_in_scan',
                text: `Invalid Parameter ID: "${propId}".`,
                assetIndex,
                componentIndex,
                messageIndex,
                propIndex,
                assetName,
                assetIp,
                componentName,
                messageName,
                editableField: 'prop_id',
                currentValue: propId,
              });
            }
          }

          // Schema-driven uniqueness checks from uniqueItemProperties on the props array.
          uniqueByFields.forEach((uniqueField: string) => {
            const fieldValue = String(get(prop, uniqueField, '')).trim();
            if (!fieldValue) return;
            const fieldValueLower = fieldValue.toLowerCase();
            const seen = seenByField.get(uniqueField)!;
            if (seen.has(fieldValueLower)) {
              const arrayErrorMsg = get(propArraySchema, 'errorMessage.uniqueItemProperties') ||
                get(propArraySchema, `errorMessage.${uniqueField}`) || '';
              const shortMsg = `${uniqueField} "${fieldValue}" is not unique${arrayErrorMsg ? '. ' + arrayErrorMsg : ''}`;
              issues.push({
                id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-p${propIndex}-${uniqueField}-not-unique`,
                severity: 'error',
                code: 'parameter_key_duplicate',
                text: `Asset "${assetLabel(assetName, assetIp, assetIndex)}", component "${componentName || 'N/A'}", message "${messageName || messageIndex + 1}", parameter ${propIndex + 1}: ${shortMsg}`.trim(),
                shortText: shortMsg,
                editableField: 'prop_field',
                editableParamField: uniqueField,
                assetIndex,
                componentIndex,
                messageIndex,
                propIndex,
                assetName,
                assetIp,
                componentName,
                messageName,
                currentValue: fieldValue,
              });
            } else {
              seen.add(fieldValueLower);
            }
          });

          // Schema-driven pattern validation for each prop field.
          Object.keys(paramProperties).forEach((fieldKey: string) => {
            const fieldSchema = paramProperties[fieldKey];
            const pattern = get(fieldSchema, 'pattern');
            if (!pattern) return;
            const fieldValue = String(get(prop, fieldKey, '')).trim();
            if (!fieldValue) return; // blank is handled by id-blank checks
            try {
              if (!new RegExp(pattern).test(fieldValue)) {
                const rawMsg = get(fieldSchema, 'errorMessage');
                const fieldErrorMsg = typeof rawMsg === 'string' ? rawMsg : `${fieldKey} must match pattern ${pattern}.`;
                issues.push({
                  id: `a${assetIndex}-c${componentIndex}-m${messageIndex}-p${propIndex}-${fieldKey}-pattern`,
                  severity: 'error',
                  code: 'parameter_field_pattern_invalid',
                  text: `Asset "${assetLabel(assetName, assetIp, assetIndex)}", component "${componentName || 'N/A'}", message "${messageName || messageIndex + 1}", parameter ${propIndex + 1}: ${fieldErrorMsg}`,
                  shortText: fieldErrorMsg,
                  editableField: 'prop_field',
                  editableParamField: fieldKey,
                  assetIndex,
                  componentIndex,
                  messageIndex,
                  propIndex,
                  assetName,
                  assetIp,
                  componentName,
                  messageName,
                  currentValue: fieldValue,
                });
              }
            } catch {
              // Invalid regex in schema — skip.
            }
          });

        });
      });
    });
  });

  return issues;
};

export const buildMessageIssueSummaryByAsset = (assets: any[]) => {
  const issues = collectMessageValidationIssues(assets);

  return (assets || []).map((asset: any, assetIndex: number) => {
    const assetName = String(get(asset, 'data.name', '')).trim();
    const assetIp = String(get(asset, 'data.ip', '')).trim();
    const assetIssues = issues.filter((issue) => issue.assetIndex === assetIndex);
    const errorCount = assetIssues.filter((issue) => issue.severity === 'error').length;
    const warningCount = assetIssues.filter((issue) => issue.severity === 'warning').length;

    return {
      assetIndex,
      assetName,
      assetIp,
      issueCount: assetIssues.length,
      errorCount,
      warningCount,
      status: errorCount > 0 ? 'Failed' : warningCount > 0 ? 'Warning' : 'Success',
    };
  });
};
